if (!customElements.get("towa-engraving")) {
  customElements.define(
    "towa-engraving",
    class TowaEngraving extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === "true") return;
        this.dataset.initialized = "true";

        this.dialog = this.querySelector("[data-engraving-dialog]");
        this.canvas = this.querySelector("[data-engraving-canvas]");
        this.input = this.querySelector("[data-engraving-input]");
        this.count = this.querySelector("[data-engraving-count]");
        this.applyButton = this.querySelector("[data-engraving-apply]");
        this.summary = this.querySelector("[data-engraving-summary]");
        this.summaryText = this.querySelector("[data-engraving-summary-text]");
        this.summaryFont = this.querySelector("[data-engraving-summary-font]");
        this.propertyText = this.querySelector("[data-property-text]");
        this.propertyFont = this.querySelector("[data-property-font]");
        this.propertySide = this.querySelector("[data-property-side]");
        this.propertyLink = this.querySelector("[data-property-link]");
        this.fontRadios = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.maxCharacters = Number(this.dataset.maxCharacters || 12);
        this.image = new Image();
        this.image.decoding = "async";
        this.image.addEventListener("load", () => this.renderPreview());
        this.image.src = this.dataset.previewImage;

        this.querySelector("[data-engraving-open]").addEventListener("click", () => this.open());
        this.querySelector("[data-engraving-edit]").addEventListener("click", () => this.open());
        this.querySelector("[data-engraving-clear]").addEventListener("click", () => this.clear());
        this.querySelector("[data-engraving-close]").addEventListener("click", () => this.cancel());
        this.querySelector("[data-engraving-cancel]").addEventListener("click", () => this.cancel());
        this.applyButton.addEventListener("click", () => this.apply());
        this.input.addEventListener("input", () => this.onInput());
        this.fontRadios.forEach((radio) => {
          radio.addEventListener("change", () => this.renderPreview());
        });
        this.dialog.addEventListener("cancel", (event) => {
          event.preventDefault();
          this.cancel();
        });
        this.dialog.addEventListener("click", (event) => {
          if (event.target === this.dialog) this.cancel();
        });

        this.updateCount();
      }

      get selectedFont() {
        return this.fontRadios.find((radio) => radio.checked) || this.fontRadios[0];
      }

      open() {
        this.openSnapshot = {
          text: this.input.value,
          font: this.selectedFont.value,
        };
        if (typeof this.dialog.showModal === "function") {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute("open", "");
        }
        document.body.classList.add("overflow-hidden");
        this.input.focus();
        this.renderPreview();
      }

      close() {
        if (typeof this.dialog.close === "function") {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute("open");
        }
        document.body.classList.remove("overflow-hidden");
      }

      cancel() {
        if (this.openSnapshot) {
          this.input.value = this.openSnapshot.text;
          const previousFont = this.fontRadios.find((radio) => radio.value === this.openSnapshot.font);
          if (previousFont) previousFont.checked = true;
        }
        this.updateCount();
        this.renderPreview();
        this.close();
      }

      onInput() {
        const characters = Array.from(this.input.value);
        if (characters.length > this.maxCharacters) {
          this.input.value = characters.slice(0, this.maxCharacters).join("");
        }
        this.updateCount();
        this.renderPreview();
      }

      updateCount() {
        const length = Array.from(this.input.value).length;
        this.count.textContent = `${length}/${this.maxCharacters}文字`;
        this.applyButton.disabled = length === 0;
      }

      async renderPreview() {
        if (!this.image.complete || !this.image.naturalWidth) return;

        const maximumCanvasWidth = 1800;
        const scale = Math.min(1, maximumCanvasWidth / this.image.naturalWidth);
        this.canvas.width = Math.round(this.image.naturalWidth * scale);
        this.canvas.height = Math.round(this.image.naturalHeight * scale);

        const context = this.canvas.getContext("2d");
        context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        context.drawImage(this.image, 0, 0, this.canvas.width, this.canvas.height);

        const text = this.input.value.trim();
        if (!text) return;

        const font = this.selectedFont.value;
        if (document.fonts && document.fonts.load) {
          try {
            await document.fonts.load(`48px ${font}`, text);
          } catch (error) {
            // The canvas falls back to the next family in the font stack.
          }
        }

        const engravingArea = {
          centerX: 0.5,
          centerY: 0.38,
          maximumWidth: 0.18,
        };
        const maximumTextWidth = this.canvas.width * engravingArea.maximumWidth;
        let fontSize = Math.round(this.canvas.height * 0.092);
        const minimumFontSize = Math.round(this.canvas.height * 0.052);
        context.font = `${fontSize}px ${font}`;
        while (context.measureText(text).width > maximumTextWidth && fontSize > minimumFontSize) {
          fontSize -= 1;
          context.font = `${fontSize}px ${font}`;
        }

        context.save();
        context.globalCompositeOperation = "multiply";
        context.fillStyle = "rgba(62, 62, 62, .52)";
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.shadowColor = "rgba(255, 255, 255, .55)";
        context.shadowBlur = 1;
        context.shadowOffsetX = 1;
        context.shadowOffsetY = 1;
        context.fillText(
          text,
          this.canvas.width * engravingArea.centerX,
          this.canvas.height * engravingArea.centerY
        );
        context.restore();
      }

      apply() {
        const text = this.input.value.trim();
        if (!text) return;

        const selectedFont = this.selectedFont;
        const linkId = this.propertyLink.value || this.createLinkId();
        this.propertyText.value = text;
        this.propertyFont.value = selectedFont.dataset.fontLabel;
        this.propertySide.value = "左側面";
        this.propertyLink.value = linkId;
        [this.propertyText, this.propertyFont, this.propertySide, this.propertyLink].forEach((input) => {
          input.disabled = false;
        });

        this.summaryText.textContent = text;
        this.summaryFont.textContent = selectedFont.dataset.fontLabel;
        this.summary.hidden = false;
        this.dataset.active = "true";
        this.openSnapshot = null;
        this.close();
      }

      clear() {
        this.input.value = "";
        this.fontRadios[0].checked = true;
        [this.propertyText, this.propertyFont, this.propertyLink].forEach((input) => {
          input.value = "";
        });
        [this.propertyText, this.propertyFont, this.propertySide, this.propertyLink].forEach((input) => {
          input.disabled = true;
        });
        this.summary.hidden = true;
        this.dataset.active = "false";
        this.updateCount();
        this.renderPreview();
      }

      createLinkId() {
        if (window.crypto && typeof window.crypto.randomUUID === "function") {
          return `engraving-${window.crypto.randomUUID()}`;
        }
        return `engraving-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }
    }
  );
}
